import { useCallback } from 'react';
import type { InfiniteData } from '@tanstack/react-query';

import { pullToRefreshHaptic } from '@/lib/haptics';
import { paginatedListState, type PaginatedListState } from '@/lib/list-state';
import { useIsOnline } from '@/lib/network';

// The query surface this hook needs, structural on purpose: any
// useInfiniteQuery result fits (typed against the caller's own page
// type), and so does the result of a B1 resource hook like
// useConnections -- screens create the query (owning its key, retry
// policy, and page shape via queryKeys/B1 hooks) and hand it here for
// the list machine.
export interface PaginatedResourceQuery<TPage> {
  data: InfiniteData<TPage, unknown> | undefined;
  error: Error | null;
  isPending: boolean;
  isError: boolean;
  refetch: () => Promise<unknown>;
  fetchNextPage: () => Promise<unknown>;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isRefetching: boolean;
  isFetchNextPageError: boolean;
}

// Everything a list screen (or ResourceListView) needs off a paginated
// query: the flattened items, which of the five list states the screen
// is in (lib/list-state.ts), and the three actions with the guards the
// screens used to re-derive inline -- pull-to-refresh fires the haptic,
// and onEndReached can fire on every scroll frame so the
// hasNextPage/isFetchingNextPage double-fire guard lives here, once.
export interface PaginatedResource<TItem> {
  items: TItem[];
  state: PaginatedListState;
  refresh: () => void;
  fetchNextPage: () => void;
  refetch: () => Promise<unknown>;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isFetchNextPageError: boolean;
  isRefetching: boolean;
}

export function usePaginatedResource<TPage, TItem>(
  query: PaginatedResourceQuery<TPage>,
  selectItems: (pages: TPage[] | undefined) => TItem[],
): PaginatedResource<TItem> {
  // Cheap flattening, recomputed per render like the inline
  // `pages.flatMap(...)` it replaces -- page arrays are tens of rows.
  const items = selectItems(query.data?.pages);
  const isOnline = useIsOnline();

  const state = paginatedListState({
    isPending: query.isPending,
    isError: query.isError,
    isOffline: !isOnline,
    hasData: query.data !== undefined,
    itemCount: items.length,
  });

  const refetch = query.refetch;
  const refresh = useCallback(() => {
    pullToRefreshHaptic();
    void refetch();
  }, [refetch]);

  const { fetchNextPage, hasNextPage, isFetchingNextPage } = query;
  const loadNextPage = useCallback(() => {
    // Guarded so this doesn't refire once the last page has been
    // fetched, and so a fast scroll can't fire it twice concurrently
    // (the guard every list screen used to inline on onEndReached).
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  return {
    items,
    state,
    refresh,
    fetchNextPage: loadNextPage,
    refetch,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError: query.isFetchNextPageError,
    isRefetching: query.isRefetching,
  };
}
